/**
 * Feature Flags Configuration
 * 
 * Controls feature availability and behavior in the application.
 * Use these flags to enable/disable features and control rollout.
 */

export const FEATURE_FLAGS = {
  // Payout Preview Control
  ENABLE_EARLY_PAYOUT_PREVIEW: false, // Disable early payout preview by default
  STRICT_USER_STORY_VALIDATION: true, // Enable strict validation against user stories
  REQUIRE_DOCUMENT_VALIDATION: true,  // Require document validation before calculation
  
  // Input Sanitization Control
  ENABLE_CLIENT_SANITIZATION: import.meta.env?.PROD || false, // Only sanitize in production
  
  // Form Behavior
  ALLOW_STEP_SKIPPING: false,        // Prevent users from skipping form steps
  PROGRESSIVE_VALIDATION: true,      // Enable progressive field validation
  
  // Calculation Features
  REAL_TIME_BENEFIT_PREVIEW: false,  // Disable real-time benefit preview
  SHOW_CALCULATION_BREAKDOWN: true,  // Show detailed calculation breakdown
  
  // Security Features
  AUDIT_PAYOUT_CALCULATIONS: true,   // Audit all payout calculations
  RATE_LIMIT_CALCULATIONS: true,     // Rate limit benefit calculations
  
  // Development Features
  DEBUG_VALIDATION_ERRORS: import.meta.env?.DEV || false, // Show detailed validation errors in dev
  MOCK_CALCULATION_DELAY: false     // Add artificial delay for testing
};

/**
 * Check if a feature flag is enabled
 */
export const isFeatureEnabled = (flagName) => {
  return FEATURE_FLAGS[flagName] === true;
};

/**
 * Get feature flag value with default fallback
 */
export const getFeatureFlag = (flagName, defaultValue = false) => {
  return FEATURE_FLAGS[flagName] !== undefined ? FEATURE_FLAGS[flagName] : defaultValue;
};

/**
 * Feature flag context for React components
 */
export const FeatureFlagContext = {
  canShowEarlyPayoutPreview: () => isFeatureEnabled('ENABLE_EARLY_PAYOUT_PREVIEW'),
  requiresStrictValidation: () => isFeatureEnabled('STRICT_USER_STORY_VALIDATION'),
  requiresDocumentValidation: () => isFeatureEnabled('REQUIRE_DOCUMENT_VALIDATION'),
  allowsStepSkipping: () => isFeatureEnabled('ALLOW_STEP_SKIPPING'),
  showsCalculationBreakdown: () => isFeatureEnabled('SHOW_CALCULATION_BREAKDOWN'),
  auditsCalculations: () => isFeatureEnabled('AUDIT_PAYOUT_CALCULATIONS')
};

/**
 * Development helper to override feature flags
 */
export const overrideFeatureFlag = (flagName, value) => {
  if (import.meta.env?.DEV) {
    FEATURE_FLAGS[flagName] = value;
    console.warn(`Feature flag ${flagName} overridden to ${value} (DEV MODE)`);
  } else {
    console.warn('Feature flag overrides are only allowed in development mode');
  }
};
